// NotificationPanel — desktop side-panel (выезжающая панель e2).
// Spec: docs/ui-spec/notifications.panel.desktop.md
//
// Тактильный редизайн «Картотека» (§8.V): выезжающая панель — поверхность e2;
// контент — date-grouped тактильный список (NotificationList).

import { Button } from "@studyhub/ui/components/button";
import { CategoryTile } from "@studyhub/ui/components/studyhub/category-tile";
import { IconTooltip } from "@studyhub/ui/components/tooltip";
import { IconBell, IconChecks, IconSettings, IconX } from "@tabler/icons-react";
import { Link } from "@tanstack/react-router";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import type { NotificationItemData } from "./notification-item";
import { NotificationList } from "./notification-list";
import { useMarkAllNotificationsRead } from "./use-mark-all-read";

interface NotificationPanelProps {
	items: NotificationItemData[];
	onClose?: () => void;
	isLoading?: boolean;
	isError?: boolean;
}

export function NotificationPanel({ items, onClose, isLoading, isError }: NotificationPanelProps) {
	const { t } = useTranslation("notifications");
	// Счётчик считаем по реальным данным, не из UI-store (там значение могло
	// разъехаться с PowerSync stream).
	const unreadCount = useMemo(() => items.filter((i) => !i.read).length, [items]);
	const markAllRead = useMarkAllNotificationsRead();

	return (
		<div className="flex h-full flex-col bg-card">
			{/* h-[62px] — совпадает с высотой основного топбара и шапки панели
			    репетитора (линия шапок выровнена). */}
			<header className="flex h-[62px] shrink-0 items-center gap-2.5 border-b border-border px-4">
				{/* Синяя категория (cards) вместо нейтрально-серой time — иконка не
				    сливается с серым фоном панели. */}
				<CategoryTile category="cards" icon={<IconBell stroke={1.75} />} size="sm" />
				<div className="min-w-0 flex-1">
					<h2 className="text-sm font-semibold text-foreground">{t("title")}</h2>
					<p className="text-2xs tabular-nums text-muted-foreground">
						{t("unreadCount", { count: unreadCount })}
					</p>
				</div>
				<div className="flex items-center gap-1.5">
					{/* «Прочитать все» — объёмная иконка-кнопка с синим акцентом, только
					    при наличии непрочитанных (иначе скрыта). Текст убран — он
					    дублировал заголовок. */}
					{unreadCount > 0 && (
						<IconTooltip label={t("markAllRead")}>
							<Button
								variant="outline"
								size="icon"
								className="cursor-pointer"
								onClick={() => void markAllRead()}
								aria-label={t("markAllRead")}
							>
								<IconChecks size={16} className="text-primary" />
							</Button>
						</IconTooltip>
					)}
					{/* Шестерёнка — объёмная (outline), как кнопка «История» в репетиторе. */}
					<IconTooltip label={t("settings")}>
						<Button asChild variant="outline" size="icon" className="cursor-pointer">
							<Link to="/settings/notifications" aria-label={t("settings")}>
								<IconSettings size={16} />
							</Link>
						</Button>
					</IconTooltip>
					{onClose && (
						<IconTooltip label={t("actions.close")}>
							<Button
								variant="ghost"
								size="icon"
								className="cursor-pointer"
								onClick={onClose}
								aria-label={t("actions.close")}
							>
								<IconX size={16} />
							</Button>
						</IconTooltip>
					)}
				</div>
			</header>

			<div className="min-h-0 flex-1 overflow-hidden">
				{/* Клик по уведомлению уводит на сущность → закрываем панель, чтобы
				    она не перекрывала открывшийся контент (паритет с mobile-sheet). */}
				<NotificationList
					items={items}
					isLoading={isLoading}
					isError={isError}
					onItemSelect={onClose}
				/>
			</div>
			{/* Нижняя полоса «Настройки уведомлений» убрана — шестерёнка настроек уже
			    есть в шапке панели справа (IconSettings → /settings/notifications). */}
		</div>
	);
}
